// externals
import { useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';

// components
import { ActionWithErrorBadge, VehicleInfoDrawer, PaymentGridToggle } from '@makemydeal/dr-dash-components';
import { Action } from '@interstate/components/Action';
import { ArrowsRightLeftIcon, ArrowTopRightOnSquareIcon, PlusIcon } from '@interstate/components/Icons';
import { Typography } from '@interstate/components/Typography';
import { useInterstateTheme } from '@interstate/components/InterstateThemeProvider';
import { AiLogoIcon } from '@makemydeal/dr-shared-components';

// selectors
import { dealerSelectors } from '@makemydeal/dr-shared-store';
import * as baseDealSelectors from '@makemydeal/dr-dash-store/selectors/BaseDealSelectors';
import * as dealModeSelectors from '@makemydeal/dr-dash-store/selectors/dealModeSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';

import { RepairOrderAlert } from './RepairOrderAlert';
import { CUSTOM_VEHICLE_LABEL, useVehicleInfo } from './useVehicleInfo';
import {
    StyledVehicleInfoCard,
    StyledVehicleInfoCardContainer,
    StyledVehicleInfoCardContent,
    StyledVehicleInfoCardBaseActionsCompact,
    LabelWithIcon,
    CompactVehicleInfoWrapper,
    CompactVehicleInfoLeft,
    CompactStyledVehicleInfoCardStockVin,
    CompactPaymentGridToggleWrapper
} from './VehicleInfo.style';

import { Box } from '@interstate/components/Box';
import { Badge } from '@interstate/components/Badge';
import { selectors as offerSelectors } from '@makemydeal/dr-offer-redux';
import { CASH } from '@makemydeal/dr-platform-types';
import { VEHICLE_ENTITY } from '@makemydeal/dr-shared-types';

export const VehicleInfoCard = () => {
    const theme = useInterstateTheme();
    const {
        title: vehicleInfoTitle,
        vin,
        stockNumber,
        stockLabel,
        mileage,
        mileageLabel: vehicleMileage,
        isCustom,
        hasVehicle,
        hasMissingVehicleError,
        isDealerAISearchEnabled,
        isShowVehicleSelection,
        changeVehicleLabel,
        changeVehicle,
        hasVAuto,
        openInVAuto
    } = useVehicleInfo();

    const labelsContainerRef = useRef<HTMLDivElement>(null);
    const stockRef = useRef<HTMLDivElement>(null);
    const vinRef = useRef<HTMLDivElement>(null);
    const customRef = useRef<HTMLDivElement>(null);

    const isDealCentralMultiScenario = useSelector(baseDealSelectors.isDealCentralMultiScenario);

    const isTemplateApplied = useSelector(offerSelectors.getIsTemplateApplied);
    const appliedTemplateName = useSelector(offerSelectors.getTemplateName);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);

    // Payment Grid Toggle visibility conditions
    const isDealCentralExperience = useSelector(dealerSelectors.isDealCentralExperience);
    const isBaseDealExperience = useSelector(dealerSelectors.isBaseDealExperience);
    const isDealCentralWithFnI = useSelector(dealerSelectors.isDealCentralWithFnI);
    const isFnIDealMode = useSelector(dealModeSelectors.isFnIDealMode);

    const isFniModeActive = isDealCentralWithFnI && isFnIDealMode;
    const isCurrentOfferTypeCash = currentOfferType === CASH;

    const isPaymentGridToggleVisible = useMemo(() => {
        return !(isFniModeActive || (isDealCentralExperience && isCurrentOfferTypeCash) || isBaseDealExperience);
    }, [isFniModeActive, isDealCentralExperience, isCurrentOfferTypeCash, isBaseDealExperience]);

    const getCardActions = () => {
        return (
            <>
                {isShowVehicleSelection && (
                    <Box>
                        <ActionWithErrorBadge entityName={VEHICLE_ENTITY} hasError={hasMissingVehicleError}>
                            <Action
                                data-testid="btnChangeVehicle"
                                size="sm"
                                onClick={changeVehicle}
                                actionIcon={{
                                    icon: hasVehicle ? <ArrowsRightLeftIcon /> : <PlusIcon data-testid="add-vehicle-icon" />,
                                    start: true
                                }}
                            >
                                {changeVehicleLabel}
                            </Action>
                        </ActionWithErrorBadge>
                        {isDealerAISearchEnabled && (
                            <LabelWithIcon data-testid="ai-assisted-vehicle-search-label-icon">
                                <span>AI Supported</span>
                                <AiLogoIcon data-testid="ai-assisted-vehicle-search-logo" fontSize="16px" ariaHidden={true} />
                            </LabelWithIcon>
                        )}
                    </Box>
                )}

                {hasVAuto && (
                    <Action
                        data-testid="vauto-link"
                        size="sm"
                        onClick={openInVAuto}
                        disabled={!hasVehicle}
                        actionIcon={{
                            icon: <ArrowTopRightOnSquareIcon />,
                            start: true
                        }}
                    >
                        vAuto
                    </Action>
                )}

                <RepairOrderAlert />
            </>
        );
    };

    const getStock = (dataTestId: string) => {
        return (
            <Typography
                tag="span"
                variant="body-sm"
                data-testid={dataTestId}
                className="vehicle-info-card__stock-label"
                ref={stockRef}
            >
                <span>{stockLabel}</span>
            </Typography>
        );
    };

    const getOdometer = () => {
        return (
            <Typography
                tag="span"
                variant="body-sm"
                data-testid="vehicle-info-card-odometer-label"
                className="vehicle-info-card-odometer-label"
            >
                <span>{vehicleMileage}</span>
            </Typography>
        );
    };

    const getVin = (dataTestId: string, vinLabel: string) => {
        return (
            <Typography tag="span" variant="body-sm" data-testid={dataTestId} className="vehicle-info-card__vin-label" ref={vinRef}>
                {vinLabel}
            </Typography>
        );
    };

    const renderCustomVehicleLabel = () => {
        return (
            isCustom && (
                <Typography
                    tag="span"
                    variant="body-sm"
                    data-testid="vehicle-info-card-custom-vehicle-label"
                    className="vehicle-info-card__custom-vehicle-label"
                    ref={customRef}
                    sx={{ marginRight: '16px' }}
                >
                    <span>{CUSTOM_VEHICLE_LABEL}</span>
                </Typography>
            )
        );
    };

    return (
        <StyledVehicleInfoCardContainer data-testid="vehicle-info-card-container">
            <StyledVehicleInfoCard
                theme={theme}
                data-testid="vehicle-info-card"
                $isDealCentralMultiScenario={isDealCentralMultiScenario}
            >
                <StyledVehicleInfoCardContent>
                    <CompactVehicleInfoWrapper>
                        <CompactVehicleInfoLeft>
                            <VehicleInfoDrawer
                                actionTitle={<span title={vehicleInfoTitle}>{vehicleInfoTitle}</span>}
                                errorBadgeHasError={hasMissingVehicleError ? false : undefined}
                                actionOverrides={{
                                    size: 'lg',
                                    actionIcon: undefined,
                                    sx: { textAlign: 'left' }
                                }}
                            />
                            <CompactStyledVehicleInfoCardStockVin
                                className="vehicle-info-card__stock-vin-container"
                                theme={theme}
                                ref={labelsContainerRef}
                            >
                                <div>
                                    {vin && getVin('vehicle-info-card-vin-base-label', vin)}
                                    {stockNumber && getStock('stock-base')}
                                    {mileage && getOdometer()}
                                    {renderCustomVehicleLabel()}
                                </div>
                                {isTemplateApplied && (
                                    <Badge
                                        data-testid="is-template-applied"
                                        variant="transparent"
                                        sx={{ maxWidth: '100%', whiteSpace: 'normal' }}
                                    >
                                        <span>{`${appliedTemplateName || ''} Template Applied`}</span>
                                    </Badge>
                                )}
                            </CompactStyledVehicleInfoCardStockVin>
                            <StyledVehicleInfoCardBaseActionsCompact $shouldAlignFlexStart={isDealerAISearchEnabled} theme={theme}>
                                <div>{getCardActions()}</div>
                                {isPaymentGridToggleVisible && (
                                    <CompactPaymentGridToggleWrapper>
                                        <Typography variant="body-sm" tag="span" color="sem.color.on-surface.muted">
                                            Payment Grid
                                        </Typography>
                                        <PaymentGridToggle currentOfferType={currentOfferType} />
                                    </CompactPaymentGridToggleWrapper>
                                )}
                            </StyledVehicleInfoCardBaseActionsCompact>
                        </CompactVehicleInfoLeft>
                    </CompactVehicleInfoWrapper>
                </StyledVehicleInfoCardContent>
            </StyledVehicleInfoCard>
        </StyledVehicleInfoCardContainer>
    );
};
